import { Cross2Icon } from '@radix-ui/react-icons';
import { Dialog as DialogPrimitive } from 'radix-ui';
import React from 'react';

import { useDialogRegistration } from '~/utils/dialogState';
import { cn } from '~/utils/styles';

interface DialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
}

interface DialogContentProps {
  children: React.ReactNode;
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  showCloseButton?: boolean;
  onEnterKey?: () => void;
  scrollable?: boolean; // defaults to true
}

interface DialogTriggerProps {
  children: React.ReactNode;
  asChild?: boolean;
}

interface DialogTitleProps {
  children: React.ReactNode;
  className?: string;
}

interface DialogDescriptionProps {
  children: React.ReactNode;
  className?: string;
}

interface DialogCloseProps {
  children: React.ReactNode;
  asChild?: boolean;
}

const Dialog = ({ open, onOpenChange, children }: DialogProps) => {
  // Register this dialog with global dialog state tracking
  useDialogRegistration(open || false);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      {children}
    </DialogPrimitive.Root>
  );
};

const DialogTrigger = ({ children, asChild = false }: DialogTriggerProps) => {
  return <DialogPrimitive.Trigger asChild={asChild}>{children}</DialogPrimitive.Trigger>;
};

const DialogContent = ({
  children,
  className,
  size = 'md',
  showCloseButton = true,
  onEnterKey,
  scrollable = true,
}: DialogContentProps) => {
  // Size classes for max-width constraints (optional)
  const sizeClasses = {
    sm: 'sm:max-w-sm',
    md: 'sm:max-w-md',
    lg: 'sm:max-w-lg',
    xl: 'sm:max-w-xl',
    '2xl': 'sm:max-w-2xl',
  };

  // Check if className contains any max-width classes
  const hasCustomMaxWidth = className && /max-w-/.test(className);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && onEnterKey) {
      // Only trigger if Enter is pressed and not on a button or textarea
      const target = event.target as HTMLElement;
      if (target.tagName !== 'BUTTON' && target.tagName !== 'TEXTAREA') {
        event.preventDefault();
        onEnterKey();
      }
    }
  };

  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="data-[state=open]:animate-overlay-in data-[state=closed]:animate-overlay-out fixed inset-0 bg-black/50 backdrop-blur-xs" />
      <DialogPrimitive.Content
        className={cn(
          'animate-dialog-bouncy-fade fixed z-50 flex w-full flex-col gap-4 border bg-white p-6 shadow-2xl',
          // Mobile: fullscreen positioning, Desktop: centered positioning
          'inset-0 sm:inset-auto sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2',
          // Mobile: fullscreen width and height
          'h-screen w-screen rounded-none',
          // Desktop: content-dependent width and height
          'sm:h-auto sm:w-auto sm:rounded-lg',
          scrollable && 'overflow-y-auto sm:max-h-[90vh]',
          // Apply max-width constraints only for non-default sizes
          size !== 'md' && !hasCustomMaxWidth && sizeClasses[size],
          className,
        )}
        onKeyDown={handleKeyDown}
      >
        <div>{children}</div>
        {showCloseButton && (
          <DialogPrimitive.Close className="absolute top-4 right-4 cursor-pointer rounded-full p-2 opacity-70 ring-offset-white transition-all duration-200 hover:bg-slate-100 hover:opacity-100 focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none sm:top-4 sm:right-4">
            <Cross2Icon className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
};

const DialogTitle = ({ children, className }: DialogTitleProps) => {
  return (
    <DialogPrimitive.Title
      className={cn('mb-2 text-lg leading-none font-semibold tracking-tight', className)}
    >
      {children}
    </DialogPrimitive.Title>
  );
};

const DialogDescription = ({ children, className }: DialogDescriptionProps) => {
  return (
    <DialogPrimitive.Description className={cn('mb-4 text-base text-gray-700', className)}>
      {children}
    </DialogPrimitive.Description>
  );
};

const DialogClose = ({ children, asChild = false }: DialogCloseProps) => {
  return <DialogPrimitive.Close asChild={asChild}>{children}</DialogPrimitive.Close>;
};

export { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle, DialogTrigger };
